/** Reader feedback on the Desk: labels and filters for the "Tell us straight" messages. */
import type { DeskFeedbackItem, FeedbackKind } from './api'
import { ApiError } from '../api/client'

export const KINDS: FeedbackKind[] = ['good', 'bad', 'ugly']
export const KIND_LABEL: Record<FeedbackKind, string> = { good: 'Good', bad: 'Bad', ugly: 'Ugly' }

const CHIP = 'inline-flex h-6 items-center rounded-full px-2.5 font-ui text-[12px] font-semibold whitespace-nowrap'
const KIND_CHIP: Record<FeedbackKind, string> = {
  good: `${CHIP} bg-radar-green/15 text-radar-green`,
  bad: `${CHIP} bg-amber-glow/20 text-amber-deep`,
  ugly: `${CHIP} bg-radar-red/15 text-radar-red`,
}
export const KIND_DOT: Record<FeedbackKind, string> = { good: 'bg-radar-green', bad: 'bg-amber-glow', ugly: 'bg-radar-red' }

export function kindChipClass(kind: string): string {
  return KIND_CHIP[kind as FeedbackKind] ?? `${CHIP} bg-ink-600 text-paper-dim`
}

/** "1 note", "9 notes" */
export function notesLabel(n: number): string {
  return `${n} ${n === 1 ? 'note' : 'notes'}`
}

/** Who sent it: "Visitor", "Member · Jane Doe", "Visitor · reader@x.com". Names and addresses reach editors only. */
export function senderLabel(m: Pick<DeskFeedbackItem, 'audience' | 'member_name' | 'email'>): string {
  const who = m.audience === 'member' ? 'Member' : 'Visitor'
  return [who, m.member_name, m.email].filter(Boolean).join(' · ')
}

export function filterByKind<T extends { kind: string }>(items: T[], kind: FeedbackKind | null): T[] {
  return kind ? items.filter((m) => m.kind === kind) : items
}

/** The subject a reply starts with. */
export function replySubject(titleName: string): string {
  return `Re: Your feedback on ${titleName}`
}

/** Where a message stands, for the line above the reply box. */
export type ReplyState = 'hold' | 'answered_back' | 'replied' | 'open' | 'no_email'
export function replyState(m: Pick<DeskFeedbackItem, 'support_hold' | 'replies' | 'answers' | 'email'>): ReplyState {
  if (m.support_hold) return 'hold'
  const lastReply = m.replies.at(-1)?.created_at ?? null
  const answers = m.answers.filter((a) => !a.auto_reply)
  const lastAnswer = answers.at(-1)?.received_at ?? null
  if (lastAnswer && (!lastReply || lastAnswer > lastReply)) return 'answered_back'
  if (m.replies.length > 0) return 'replied'
  return m.email ? 'open' : 'no_email'
}

/** The server's 10-minute repeat check: ask, then send again with confirm_repeat. */
export function isRepeatPrompt(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && /Send another\?/.test(String((err.detail as { detail?: unknown } | null)?.detail ?? ''))
}

/** "jane@airage.com" -> "jane" for the "Replied by" line. */
export function staffName(sentBy: string): string {
  return sentBy.split('@')[0] || sentBy
}
